import type {
  AccountsBalance,
  Balance,
  BalancesByChain,
  BalancesSummary,
  WalletGroupedAccounts,
} from '@polaris/core/models'
import { formatCoin, formatPrice, shorten } from '@polaris/core/utils'
import { useAtomValue } from 'jotai'
import {
  type MouseEventHandler,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { flatMap, map, pipe, sortBy } from 'remeda'
import { useUserSettings } from '../hooks/use-user-settings'
import { accountsFilteredBalancesAtom } from '../stores/balances/accounts'
import { cn } from '../utils/styles'
import { WalletIcon } from './accounts/wallet-icon'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion'
import { ChainImg } from './ui/chain-img'
import { CounterBadge } from './ui/counter-badge'
import {
  ListRowButton,
  ListRowImage,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from './ui/list-row'

// #region Chain Balances

interface ChainBalancesAccordionsProps {
  onClickBalance?: (balance: Balance) => void
  onHoverBalance?: (balance: Balance) => void
}

export function ChainBalancesAccordions({
  onClickBalance,
  onHoverBalance,
}: ChainBalancesAccordionsProps) {
  const [openItems, setOpenItems] = useState<string | undefined>()
  const { balancesByChains } = useAtomValue(accountsFilteredBalancesAtom)

  return (
    <Accordion
      type='single'
      collapsible
      value={openItems}
      onValueChange={setOpenItems}
    >
      {balancesByChains.map((balancesByChain) => (
        <ChainBalancesAccordionItem
          key={balancesByChain.chain_id}
          {...balancesByChain}
          onClickBalance={onClickBalance}
          onHoverBalance={onHoverBalance}
        />
      ))}
    </Accordion>
  )
}

interface ChainBalancesAccordionItemProps extends BalancesByChain {
  onClickBalance?: (balance: Balance) => void
  onHoverBalance?: (balance: Balance) => void
}

const ChainBalancesAccordionItem = ({
  chain_name,
  chain_img,
  chain_id,
  totalUsdValue,
  balances,
  addresses,
  onClickBalance,
  onHoverBalance,
}: ChainBalancesAccordionItemProps) => {
  const { userSettings } = useUserSettings()

  return (
    <AccordionItem
      value={chain_name}
      className={cn(
        'border-b-transparent group/accordion',
        'data-[state=open]:bg-surface1',
        'data-[state=open]:rounded-2xl',
        '[&_div[data-component=uncollapsed-balance-info]]:data-[state=open]:flex',
        '[&_div[data-component=collapsed-balance-info]]:data-[state=open]:hidden',
        '[&_button[data-component=balance-info]]:data-[state=open]:!pb-0',
      )}
    >
      <AccordionTrigger asChild>
        <ListRowButton
          data-component='balance-info'
          className='group/balance justify-stretch py-2 group-data-[state=open]/accordion:pt-4 group-data-[state=open]/accordion:hover:!bg-transparent'
          size='md'
        >
          <div
            data-component='uncollapsed-balance-info'
            className='hidden items-center justify-between w-full pb-4'
          >
            <p className='text-secondary text-sm'>{chain_name}</p>
            <span className='link group-hover/balance:text-link-hover text-xs sm:text-sm font-semibold'>
              Hide balances
            </span>
          </div>

          <div data-component='collapsed-balance-info'>
            <div className='relative'>
              {chain_img && (
                <ChainImg
                  chainImg={chain_img}
                  chainName={chain_name}
                  chainId={chain_id}
                  unoptimized
                  size='lg'
                  containerClassName='size-10 min-w-10'
                />
              )}
              <CounterBadge
                ringClassName='ring-background group-hover/balance:!ring-surface1-100'
                countClassName='group-hover/balance:opacity-0'
                iconClassName='group-hover/balance:opacity-100'
                count={balances.length}
              />
            </div>
          </div>

          <div
            data-component='collapsed-balance-info'
            className='flex flex-col flex-1 gap-1'
          >
            <ListRowSide
              className='flex justify-between flex-row w-full'
              containerClassName='w-full'
            >
              <div className='relative'>
                <ListRowSideName className='max-w-48 truncate transition-opacity block'>
                  {chain_name}
                </ListRowSideName>
              </div>
              <ListRowSideName
                className={cn('text-base font-normal', {
                  'h-6': userSettings.maskBalances,
                })}
              >
                {!userSettings.maskBalances ? (
                  formatPrice(totalUsdValue)
                ) : (
                  <span className='text-tertiary-100 text-3xl h-6 font-normal'>
                    *****
                  </span>
                )}
              </ListRowSideName>
            </ListRowSide>

            <ListRowSide
              className='flex justify-between flex-row w-full'
              containerClassName='w-full'
            >
              <ListRowSideDescription className='whitespace-nowrap overflow-hidden text-ellipsis max-w-44'>
                {addresses.length === 1 ? (
                  <>
                    <span className='md:hidden'>
                      {shorten(addresses[0], { suffixLength: 0 })}
                    </span>
                    <span className='max-md:hidden'>
                      {shorten(addresses[0])}
                    </span>
                  </>
                ) : (
                  <span className='max-md:hidden'>
                    {addresses.length} accounts
                  </span>
                )}
              </ListRowSideDescription>
              <ListRowSideDescription className='text-right'>
                <span className='text-link-hover font-semibold whitespace-nowrap group-hover/balance:opacity-100 opacity-0 transition-opacity'>
                  Show balances
                </span>
              </ListRowSideDescription>
            </ListRowSide>
          </div>
        </ListRowButton>
      </AccordionTrigger>
      <AccordionContent>
        {balances.map((balance) => (
          <BalanceListRowButton
            key={`${balance.denom}-${balance.address}`}
            {...balance}
            onClick={() => onClickBalance?.(balance)}
            onHover={() => onHoverBalance?.(balance)}
            hideChainImg
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  )
}

// #region Summaries

interface BalanceSummariesAccordionsProps {
  onClickBalance?: (balance: AccountsBalance) => void
  onHoverBalance?: (balance: AccountsBalance) => void
  balancesSummary?: BalancesSummary[]
  className?: string
  externalOpenItem?: string
  setExternalOpenItem?: (value: string) => void
}

/** Balances grouped by kind into list of accordions containing summary info. */
export function BalanceSummariesAccordions({
  onClickBalance,
  onHoverBalance,
  balancesSummary: balancesSummaryProp,
  className,
  externalOpenItem,
  setExternalOpenItem,
}: BalanceSummariesAccordionsProps) {
  const { balancesSummary: filteredBalancesSummary } = useAtomValue(
    accountsFilteredBalancesAtom,
  )

  const [openItem, setOpenItem] = useState<string>()

  return (
    <Accordion
      type='single'
      collapsible
      value={externalOpenItem ?? openItem}
      onValueChange={setExternalOpenItem ?? setOpenItem}
      className={className}
    >
      {(balancesSummaryProp ?? filteredBalancesSummary).map(
        (balanceSummary, i) => (
          <BalanceSummaryAccordionItem
            key={`${balanceSummary.summary.display_name}-${i}`}
            {...balanceSummary}
            onClickBalance={onClickBalance}
            onHoverBalance={onHoverBalance}
          />
        ),
      )}
    </Accordion>
  )
}

interface BalanceSummaryAccordionItemProps extends BalancesSummary {
  onClickBalance?: (balance: AccountsBalance) => void
  onHoverBalance?: (balance: AccountsBalance) => void
}

const BalanceSummaryAccordionItem_ = ({
  summary: {
    display_name,
    normalized_amount,
    tickers,
    logo_img_url,
    value_usd,
    unique_logo_img_urls,
  },
  balances,
  onClickBalance,
  onHoverBalance,
}: BalanceSummaryAccordionItemProps) => {
  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (balances.length === 1) {
        e.preventDefault()
        onClickBalance?.(balances[0])
        return
      }
    },
    [balances, onClickBalance],
  )

  const handleHover: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      if (balances.length === 1) {
        e.preventDefault()
        onHoverBalance?.(balances[0])
        return
      }
    },
    [balances, onHoverBalance],
  )

  const { userSettings } = useUserSettings()

  const MemoizedNameAndValueUSD = useMemo(
    () => (
      <ListRowSide
        className='flex justify-between flex-row w-full'
        containerClassName='w-full'
      >
        <div className='relative'>
          <ListRowSideName className='max-w-20 md:max-w-48 truncate transition-opacity block text-base'>
            {display_name}
          </ListRowSideName>
        </div>
        <ListRowSideName
          className={cn('text-base font-normal', {
            'h-6': userSettings.maskBalances,
          })}
        >
          {!userSettings.maskBalances ? (
            formatPrice(value_usd)
          ) : (
            <span className='text-tertiary-100 text-3xl h-6 font-normal'>
              *****
            </span>
          )}
        </ListRowSideName>
      </ListRowSide>
    ),
    [display_name, userSettings.maskBalances, value_usd],
  )

  const MemoizedTickersAndAmounts = useMemo(
    () => (
      <ListRowSide
        className='flex justify-between flex-row w-full'
        containerClassName='w-full'
      >
        <ListRowSideDescription className='whitespace-nowrap overflow-hidden text-ellipsis max-w-44 flex gap-1 font-normal'>
          <span className='max-w-20 md:max-w-32 truncate'>
            {Array.from(new Set(tickers)).join(', ')}
          </span>
          {balances.length === 1 && (
            <span className='opacity-0 hidden group-hover/balance:opacity-100 group-hover/balance:block md:block transition-opacity'>
              on {balances[0].chain_name}
            </span>
          )}
        </ListRowSideDescription>
        <ListRowSideDescription
          className={cn('whitespace-nowrap', {
            'h-5': userSettings.maskBalances,
          })}
        >
          {balances.length > 1 ? (
            <span className='text-link-hover text-xs md:text-sm font-semibold group-hover/balance:opacity-100 opacity-0 transition-opacity'>
              Show balances
            </span>
          ) : (
            !userSettings.maskBalances &&
            formatCoin({ amount: normalized_amount })
          )}
        </ListRowSideDescription>
      </ListRowSide>
    ),
    [balances, userSettings.maskBalances, normalized_amount, tickers],
  )

  return (
    <AccordionItem
      value={display_name}
      data-testid='balances'
      className={cn(
        'border-b-transparent group/accordion',
        'data-[state=open]:bg-surface1',
        'data-[state=open]:rounded-2xl',
        '[&_div[data-component=uncollapsed-balance-info]]:data-[state=open]:flex',
        '[&_div[data-component=collapsed-balance-info]]:data-[state=open]:hidden',
        '[&_button[data-component=balance-info]]:data-[state=open]:!pb-0',
      )}
    >
      <AccordionTrigger
        asChild
        onClick={handleClick}
        onMouseEnter={handleHover}
      >
        <ListRowButton
          data-component='balance-info'
          className={cn(
            'group/balance justify-stretch py-2 group-data-[state=open]/accordion:pt-4 group-data-[state=open]/accordion:hover:!bg-transparent',
            {
              'group/chain': balances.length === 1,
            },
          )}
          size='md'
        >
          <div
            data-component='uncollapsed-balance-info'
            className='hidden items-center justify-between w-full pb-4'
          >
            <p className='text-secondary text-sm'>{display_name}</p>
            <span className='link group-hover/balance:text-link-hover text-xs sm:text-sm font-semibold'>
              Hide balances
            </span>
          </div>

          <div data-component='collapsed-balance-info'>
            {balances.length > 1 ? (
              <div className='relative flex-shrink-0'>
                <MultiBalancesImage
                  logoUrls={unique_logo_img_urls}
                  numBalances={balances.length}
                  className='min-w-10 size-10'
                />
              </div>
            ) : (
              <div className='relative'>
                <ListRowImage
                  src={logo_img_url}
                  alt={`${display_name} icon`}
                  width={40}
                  height={40}
                  className='rounded-full group-hover/chain:opacity-0 transition-opacity size-10 min-w-10'
                  classes={{
                    container: 'flex-shrink-0',
                  }}
                  unoptimized
                  placeholderName={display_name}
                />
                <ListRowImage
                  src={logo_img_url}
                  alt={`${display_name} icon`}
                  width={40}
                  height={40}
                  className='rounded-full'
                  classes={{
                    container:
                      'flex-shrink-0 absolute left-0 bottom-0 group-hover/chain:opacity-100 opacity-0 transition-opacity',
                  }}
                  chainImageSrc={balances[0].chain_img}
                  chainImageAlt={`${balances[0].chain_name} image`}
                  unoptimized
                  placeholderName={balances[0].chain_name}
                  chainId={balances[0].chain_id}
                />
              </div>
            )}
          </div>

          <div
            data-component='collapsed-balance-info'
            className='flex flex-col flex-1 gap-1'
          >
            {MemoizedNameAndValueUSD}
            {MemoizedTickersAndAmounts}
          </div>
        </ListRowButton>
      </AccordionTrigger>
      <AccordionContent>
        {balances.map((balance) => (
          <BalanceListRowButton
            key={balance.chain_id + balance.denom}
            onClick={() => onClickBalance?.(balance)}
            onHover={() => onHoverBalance?.(balance)}
            {...balance}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  )
}

const BalanceSummaryAccordionItem = memo(BalanceSummaryAccordionItem_)

// #region Wallet Groups

interface WalletGroupsAccordionsProps {
  onClickBalance?: (balance: AccountsBalance) => void
  onHoverBalance?: (balance: AccountsBalance) => void
  walletGroups: WalletGroupedAccounts[]
  className?: string
  externalOpenItem?: string
  setExternalOpenItem?: (value: string) => void
}

/** Balances grouped by wallet into list of accordions containing wallet info. */
export function WalletGroupsAccordions({
  onClickBalance,
  onHoverBalance,
  walletGroups,
  className,
  externalOpenItem,
  setExternalOpenItem,
}: WalletGroupsAccordionsProps) {
  const [openItem, setOpenItem] = useState<string>()

  return (
    <Accordion
      type='single'
      collapsible
      value={externalOpenItem ?? openItem}
      onValueChange={setExternalOpenItem ?? setOpenItem}
      className={className}
    >
      {walletGroups.map((walletGroup) => (
        <WalletGroupAccordionItem
          key={walletGroup.grouping_key}
          {...walletGroup}
          onClickBalance={onClickBalance}
          onHoverBalance={onHoverBalance}
        />
      ))}
    </Accordion>
  )
}

interface WalletGroupAccordionItemProps extends WalletGroupedAccounts {
  onClickBalance?: (balance: AccountsBalance) => void
  onHoverBalance?: (balance: AccountsBalance) => void
}

const WalletGroupAccordionItem = ({
  name,
  type,
  provider_info,
  grouping_key,
  totalValue,
  balances,
  accounts,
  onClickBalance,
  onHoverBalance,
}: WalletGroupAccordionItemProps) => {
  const { userSettings } = useUserSettings()
  return (
    <AccordionItem
      value={grouping_key}
      data-testid='wallet-group'
      className={cn(
        'border-b-transparent group/accordion',
        'data-[state=open]:bg-surface1',
        'data-[state=open]:rounded-2xl',
        '[&_div[data-component=uncollapsed-wallet-info]]:data-[state=open]:flex',
        '[&_div[data-component=collapsed-wallet-info]]:data-[state=open]:hidden',
        '[&_button[data-component=wallet-info]]:data-[state=open]:!pb-0',
      )}
    >
      <AccordionTrigger asChild>
        <ListRowButton
          data-component='wallet-info'
          className='group/wallet group/balance justify-stretch py-2 group-data-[state=open]/accordion:pt-4 group-data-[state=open]/accordion:hover:!bg-transparent'
          size='md'
        >
          <div
            data-component='uncollapsed-wallet-info'
            className='hidden items-center justify-between w-full pb-4'
          >
            <p className='text-secondary text-sm'>{name}</p>
            <span className='link group-hover/balance:text-link-hover text-xs sm:text-sm font-semibold'>
              Hide balances
            </span>
          </div>

          <div data-component='collapsed-wallet-info'>
            <div className='relative flex-shrink-0'>
              <WalletIcon
                logoUrl={provider_info.img}
                walletName={provider_info.name}
                walletType={type}
                size={40}
                className='size-10 min-w-10'
              />
              <CounterBadge
                ringClassName='ring-background group-hover/balance:!ring-surface1-100'
                countClassName='group-hover/balance:opacity-0'
                iconClassName='group-hover/balance:opacity-100'
                count={accounts.length}
              />
            </div>
          </div>

          <div
            data-component='collapsed-wallet-info'
            className='flex flex-col flex-1 gap-1'
          >
            <ListRowSide
              className='flex justify-between flex-row w-full'
              containerClassName='w-full'
            >
              <div className='relative'>
                <ListRowSideName className='max-w-20 md:max-w-48 truncate transition-opacity block text-base'>
                  {name}
                </ListRowSideName>
              </div>
              <ListRowSideName
                className={cn('text-base font-normal', {
                  'h-6': userSettings.maskBalances,
                })}
              >
                {!userSettings.maskBalances ? (
                  formatPrice(totalValue)
                ) : (
                  <span className='text-tertiary-100 text-3xl h-6 font-normal'>
                    *****
                  </span>
                )}
              </ListRowSideName>
            </ListRowSide>

            <ListRowSide
              className='flex justify-between flex-row w-full'
              containerClassName='w-full'
            >
              <ListRowSideDescription className='whitespace-nowrap overflow-hidden text-ellipsis max-w-44 font-normal'>
                <span className='max-w-20 md:max-w-32 truncate'>
                  {type === 'WatchAddress' ? 'Watching' : provider_info.name}
                </span>
              </ListRowSideDescription>
              <ListRowSideDescription className='whitespace-nowrap'>
                <span className='text-link-hover text-xs md:text-sm font-semibold group-hover/wallet:opacity-100 opacity-0 transition-opacity'>
                  Show balances
                </span>
              </ListRowSideDescription>
            </ListRowSide>
          </div>
        </ListRowButton>
      </AccordionTrigger>
      <AccordionContent>
        {pipe(
          balances,
          flatMap((balanceSummary) => balanceSummary.balances),
          sortBy([({ value_usd }) => value_usd ?? 0, 'desc']),
          map((balance) => (
            <BalanceListRowButton
              key={balance.chain_id + balance.denom}
              onClick={() => onClickBalance?.(balance)}
              onHover={() => onHoverBalance?.(balance)}
              {...balance}
            />
          )),
        )}
      </AccordionContent>
    </AccordionItem>
  )
}

export const BalanceListRowButton = ({
  normalized_amount,
  chain_id,
  denom,
  chain_name,
  ticker,
  logo_img_url,
  value_usd,
  chain_img,
  display_name,
  hideChainImg,
  showIndicator,
  onClick,
  onHover,
}: (AccountsBalance | Balance) & {
  onClick?: () => void
  onHover?: () => void
  hideChainImg?: boolean
  showIndicator?: boolean
}) => {
  /**
   * For bitcoin we have two kind of balance, one is the one that is spendable,
   * the other one is the balance that's in pending due to some tx performed by the user
   */
  const isPendingBitcoinBalance = denom === 'utxo-native-pending'
  /**
   * For hyperliquid perps we have two kind of balance none of the balance is directly spendable on
   * our frontend currently.
   * See docs: https://github.com/polaris-portal/polaris/blob/main/app/route-providers/hyperliquid/README.md
   */
  const isHyperliquidPerpsBalance = denom.endsWith('-hlperp')

  const { userSettings } = useUserSettings()

  return (
    <ListRowButton
      onClick={onClick}
      onMouseEnter={onHover}
      size='md'
      disabled={isPendingBitcoinBalance || isHyperliquidPerpsBalance}
    >
      {logo_img_url && (
        <ListRowImage
          src={logo_img_url}
          alt={`${ticker} icon`}
          width={40}
          height={40}
          className={cn('rounded-full size-10 min-w-10', {
            '!size-7 !min-w-7': isPendingBitcoinBalance,
          })}
          classes={{ container: 'flex-shrink-0' }}
          unoptimized
          chainImageAlt={!hideChainImg ? chain_name : undefined}
          chainImageSrc={!hideChainImg ? chain_img : undefined}
          chainId={chain_id}
          placeholderName={display_name}
          pending={isPendingBitcoinBalance}
        />
      )}
      <ListRowSide containerClassName='w-full' className='gap-0'>
        <ListRowSideName className='text-base'>{ticker}</ListRowSideName>
        <ListRowSideDescription className='leading-6'>
          on {chain_name}
        </ListRowSideDescription>
      </ListRowSide>
      <ListRowSide className='text-right gap-0' showIndicator={showIndicator}>
        <ListRowSideName
          className={cn('text-base font-normal', {
            'h-6': userSettings.maskBalances,
          })}
        >
          {!userSettings.maskBalances ? (
            formatPrice(value_usd)
          ) : (
            <span className='text-tertiary-100 text-3xl h-6 font-normal'>
              *****
            </span>
          )}
        </ListRowSideName>
        <ListRowSideDescription
          className={cn('leading-6', {
            'h-6': userSettings.maskBalances,
          })}
        >
          {!userSettings.maskBalances &&
            formatCoin({
              amount: normalized_amount,
            })}
        </ListRowSideDescription>
      </ListRowSide>
    </ListRowButton>
  )
}

interface MultiBalancesImageProps {
  logoUrls: string[]
  numBalances?: number
  className?: string
}

export const MultiBalancesImage = ({
  logoUrls,
  numBalances,
  className,
}: MultiBalancesImageProps) => {
  let imageContent: React.ReactNode
  if (logoUrls.length === 1) {
    imageContent = (
      <ListRowImage
        src={logoUrls[0]}
        alt=''
        width={40}
        height={40}
        className={cn('rounded-full size-12', className)}
        classes={{ container: cn('flex-shrink-0') }}
        unoptimized
      />
    )
  } else if (logoUrls.length === 2) {
    imageContent = (
      <div className={cn('relative size-12', className)}>
        <ListRowImage
          src={logoUrls[0]}
          alt=''
          width={24}
          height={24}
          className='rounded-full'
          classes={{
            container: cn('flex-shrink-0 absolute top-0 right-0'),
          }}
          unoptimized
        />
        <ListRowImage
          src={logoUrls[1]}
          alt=''
          width={24}
          height={24}
          className='rounded-full'
          classes={{
            container: cn('flex-shrink-0 absolute bottom-0 left-0'),
          }}
          unoptimized
        />
      </div>
    )
  } else if (logoUrls.length >= 3) {
    imageContent = (
      <div className={cn('grid grid-cols-2 gap-1 size-12', className)}>
        {logoUrls.slice(0, 4).map((logoUrl, index) => (
          <ListRowImage
            key={index}
            src={logoUrl}
            alt=''
            width={16}
            height={16}
            className='rounded-full'
            unoptimized
          />
        ))}
      </div>
    )
  }

  return (
    <div className='relative group-balance'>
      {imageContent}
      <CounterBadge
        count={numBalances ?? 0}
        ringClassName='ring-background group-hover/balance:!ring-surface1-100'
        countClassName='group-hover/balance:opacity-0'
        iconClassName='group-hover/balance:opacity-100'
      />
    </div>
  )
}
